import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { works } from '@/mocks/works';
import { articles } from '@/mocks/articles';
import { profileData } from '@/mocks/profile';

export default function ArticlePage() {
  const { articleId = '' } = useParams<{ articleId: string }>();

  const work = works.find((w) => w.id === articleId);
  const article = articles.find((a) => a.id === articleId);
  const item = work || article;

  useEffect(() => {
    if (item) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [articleId, item]);

  if (!item) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">内容未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">您访问的内容不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const isWork = 'publisher' in item;
  const pageTitle = `${item.title} - 佐佐木明希${isWork ? '作品详情' : '动态详情'}`;
  const pageDesc = isWork ? item.description : item.summary;
  const pageDate = item.publishDate || (isWork ? item.releaseDate : '');

  const articleJsonLd = isWork
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: item.title,
        description: item.description,
        datePublished: item.releaseDate,
        author: { '@type': 'Organization', name: '佐佐木明希粉丝站' },
        publisher: { '@type': 'Organization', name: item.publisher },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: item.title,
        description: item.summary,
        datePublished: item.publishDate,
        author: { '@type': 'Person', name: item.author },
        publisher: { '@type': 'Organization', name: '佐佐木明希粉丝站' },
      };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`佐佐木明希,${item.title},佐佐木明希作品,佐佐木明希动态`}
        canonicalUrl={`https://akiupdate.github.io/article/${articleId}`}
        ogType="article"
      />
      <div className="pt-20">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              {
                label: isWork ? '作品全览' : '最新动态',
                href: isWork ? '/category/all' : '/category/news',
              },
              { label: item.title },
            ]}
          />
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8" itemScope itemType="https://schema.org/Article">
          {/* Header */}
          <header className="mb-8">
            {!isWork && (
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium mb-4">
                {'category' in item ? item.category : ''}
              </span>
            )}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight" itemProp="headline">
              {item.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 font-label">
              <time dateTime={pageDate} itemProp="datePublished">{pageDate}</time>
              {!isWork && 'author' in item && (
                <>
                  <span>·</span>
                  <span itemProp="author">{item.author}</span>
                </>
              )}
              {isWork && (
                <>
                  <span>·</span>
                  <span>{item.publisher}</span>
                  <span>·</span>
                  <span>{item.duration}</span>
                </>
              )}
            </div>
            {!isWork && 'tags' in item && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {item.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Cover Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={'coverImage' in item ? item.coverImage : item.coverImage || ''}
              alt={item.title}
              title={`佐佐木明希 - ${item.title}`}
              className="w-full aspect-[16/10] object-cover"
              itemProp="image"
            />
          </div>

          {/* Content */}
          {!isWork && 'content' in item ? (
            <div
              className="prose-content"
              itemProp="articleBody"
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          ) : (
            <div className="space-y-6" itemProp="description">
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                {item.description}
              </p>
              {isWork && (
                <div className="bg-background-100 rounded-xl p-6 mt-8">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-4 text-base">作品信息</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: '作品名称', value: item.title },
                      { label: '日文名称', value: item.titleJa },
                      { label: '发行日期', value: item.releaseDate },
                      { label: '发行公司', value: item.publisher },
                      { label: '作品时长', value: item.duration },
                      { label: '主演', value: profileData.name },
                    ].map((info) => (
                      <div key={info.label}>
                        <span className="block text-xs text-foreground-400 font-label mb-1">{info.label}</span>
                        <span className="text-sm text-foreground-900 font-medium">{info.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Related Links */}
          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to={isWork ? '/category/all' : '/category/news'}
                className="inline-flex items-center gap-2 text-foreground-600 text-sm font-label hover:text-primary-500 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {isWork ? '返回作品列表' : '返回动态列表'}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-foreground-400 text-xs font-label">分享：</span>
                <button
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                  aria-label="分享到Twitter"
                >
                  <i className="ri-twitter-x-line"></i>
                </button>
                <button
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                  aria-label="复制链接"
                >
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}